import React, { useState, useRef } from 'react';
import { Plus, Minus, FileText } from 'lucide-react';
import { Button } from '@headlessui/react';
import { Task } from 'types/project/tasks';
import { useGetAllTasksQuery } from 'service/project/hooks/useGetAllTasksQuery';
import { useProjectQuery } from 'service/project';
import { useLinkWorkItemsMutation } from 'service/project/hooks/useLinkWorkItemsMutation';
import { Combobox, ComboboxInput, ComboboxOptions, ComboboxOption, ComboboxButton } from '@headlessui/react';
import { WorkType } from 'types/project/projectDefaults';
import { workTypeUIMap } from './Dropdowns/TaskTypeDropdown';
import { LinkType } from './Dropdowns/LinkTypeDropdown';
import AddTaskTableLayer from './ProjectDetails/AddTaskTableLayer';
import { User } from 'types/task';
import { useDebounce } from 'hooks/useDebounce';
import { useUnlinkWorkItemsMutation } from 'service/project/hooks/useUnlinkWorkItemsMutation';
import { toast } from 'react-hot-toast';
import { GoUnlink } from "react-icons/go";

export interface LinkedItemDraft {
  id: string; // temporary id for UI mapping
  targetWorkItemUuid: string; // The URL/UUID picked
  sourceWorkItemUuid?: string; // The source for existing links
  linkedWorkItemUuid?: string; // The ID of the link itself from the backend
  linkType: string;
  // Optionally populated when hydrated from the backend response:
  resolvedTask?: {
    workItemUuid: string;
    workItemKey: string;
    title?: string;
    workItemTitle?: string;
    workTypeCategory?: string;
    workTypeUuid?: string;
    workTypeName?: string;
    workStatusUuid?: string;
    workStatusName?: string;
    workStatusCategory?: string;
    workPriority?: string;
    workItemAssigneeAppUserUuid?: string;
  };
}

interface LinkedItemsListProps {
  linkedItems: LinkedItemDraft[];
  onLinksChange: (items: LinkedItemDraft[]) => void;
  availableWorkTypes: WorkType[];
  disabled?: boolean;
  /** Lists the links only: no linking or unlinking. */
  readOnly?: boolean;
  currentTaskUuid?: string;
  projectKey?: string;
  projectId?: string;
}

const LinkedItemsList: React.FC<LinkedItemsListProps> = ({
  linkedItems,
  onLinksChange,
  availableWorkTypes,
  disabled = false,
  readOnly = false,
  currentTaskUuid,
  projectKey,
  projectId,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const debouncedSearch = useDebounce(inputValue, 500);
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedLinkType] = useState<LinkType>('IS_BLOCKED_BY');
  const [validationError, setValidationError] = useState<string | null>(null);
  const comboboxBtnRef = useRef<HTMLButtonElement>(null);
  
  // Fetch existing tasks using basic params for dropdown (pagination size = 100 for now)
  const { data: taskResponse } = useGetAllTasksQuery({ 
    projectUuid: projectId || '', 
    search: debouncedSearch || undefined,
    cursor: null, 
    size: 100,
    viewType: 'LIST'
  });
  const tasksList = taskResponse?.response?.[0]?.data || []; 

  // Reset selection and input when section is closed
  React.useEffect(() => {
    if (!isExpanded) {
      setInputValue('');
      setSelectedTask(null);
    }
  }, [isExpanded]);

  // Mutations for linking and unlinking
  const { mutate: linkTask } = useLinkWorkItemsMutation();
  const { mutate: unlinkTask, isPending: isUnlinking } = useUnlinkWorkItemsMutation();

  // Convert available ProjectUsers to Users for the table dropdowns
  const { data: project } = useProjectQuery(projectId);
  const mappedUsers: User[] = (project?.appUserList || []).map((u: any) => ({
    id: u.appUserUuid,
    appUserUuid: u.appUserUuid,
    name: `${u.firstName || ''} ${u.lastName || ''}`.trim(),
    avatar: u.avatar || '',
    email: '' 
  }));

  const handleAddLink = () => {
    if (!inputValue.trim()) return;
    setValidationError(null);

    // Check if the input exactly matches an existing task by key or title
    const existingMatch = tasksList.find(t => {
      const displayString = `${t.workItemKey} ${t.title || t.workItemTitle || ''}`.trim().toLowerCase();
      const inputLower = inputValue.trim().toLowerCase();
      return t.workItemKey.toLowerCase() === inputLower 
        || (t.title || t.workItemTitle)?.toLowerCase() === inputLower
        || displayString === inputLower;
    });

    // ── Validation: block random/free text ──────────────────────────────────────
    // Only allow linking if user explicitly picked from dropdown OR input exactly matches a real task
    if (!selectedTask && !existingMatch) {
      setValidationError('No matching work item found. Please select from the list.');
      return;
    }
    
    // Prioritize explicitly selected task from the dropdown
    const targetUuid = selectedTask ? selectedTask.workItemUuid : existingMatch!.workItemUuid;
    
    // Prevent self-linking
    if (currentTaskUuid && targetUuid === currentTaskUuid) {
      setInputValue('');
      setSelectedTask(null);
      return;
    }

    // Prevent duplicate linking
    const isAlreadyLinked = linkedItems.some(item => 
      item.targetWorkItemUuid === targetUuid || 
      item.sourceWorkItemUuid === targetUuid ||
      item.id === targetUuid
    );

    if (isAlreadyLinked) {
      toast.error('This work item is already linked.');
      setInputValue('');
      setSelectedTask(null);
      return;
    }

    const isUrl = targetUuid.startsWith('http://') || targetUuid.startsWith('https://');
    
    // Use the user's selected link type
    const linkType = selectedLinkType;

    // Instant backend connection scenario
    // We treat it as an existing match if we have an explicit selectedTask OR a matched task in the current result list
    const isInternalTask = !isUrl && (selectedTask || existingMatch);
    
    if (currentTaskUuid && isInternalTask) {
      linkTask({
        sourceWorkItemUuid: currentTaskUuid,
        targetWorkItemUuid: targetUuid,
        linkType: linkType
      }, {
        onSuccess: (response: any) => {
          // Add to local state only after successful backend link
          const newLink: LinkedItemDraft = {
            id: targetUuid,
            targetWorkItemUuid: targetUuid, 
            sourceWorkItemUuid: currentTaskUuid,
            linkedWorkItemUuid: response.response, // Capture the new link UUID
            linkType: linkType,
          };
          onLinksChange([...linkedItems, newLink]);
          setInputValue('');
          setSelectedTask(null);
          setValidationError(null);
        }
      });
    } else {
      // Local draft only (new task creation — task already validated above so it's a real match)
      const newLink: LinkedItemDraft = {
        id: Date.now().toString(),
        targetWorkItemUuid: targetUuid, 
        linkType: linkType,
      };

      onLinksChange([...linkedItems, newLink]);
      setInputValue('');
      setSelectedTask(null);
      setValidationError(null);
    }
  };

  const handleRemoveLink = (item: LinkedItemDraft) => {
    // If it's an existing backend link, trigger the unlink mutation
    if (item.linkedWorkItemUuid && item.sourceWorkItemUuid && item.targetWorkItemUuid) {
       unlinkTask({
         linkedWorkItemUuid: item.linkedWorkItemUuid,
         sourceWorkItemUuid: item.sourceWorkItemUuid,
         targetWorkItemUuid: item.targetWorkItemUuid
       }, {
         onSuccess: () => {
            onLinksChange(linkedItems.filter((li) => li.id !== item.id));
         }
       });
    } else {
      // For drafts or unsaved links, just remove from local state
      onLinksChange(linkedItems.filter((li) => li.id !== item.id));
    }
  };

  const filteredTasks = tasksList.filter((task) => {
    // Prevent self-linking in search
    if (currentTaskUuid && task.workItemUuid === currentTaskUuid) return false;
    
    // Prevent selecting already linked tasks
    const isAlreadyLinked = linkedItems.some(item => 
      item.targetWorkItemUuid === task.workItemUuid || 
      item.sourceWorkItemUuid === task.workItemUuid ||
      item.id === task.workItemUuid
    );
    if (isAlreadyLinked) return false;

    return true;
  });

  // Group items: prefer resolved task embedded in the draft, fall back to tasksList lookup
  const internalTasks: Task[] = linkedItems
    .map(item => {
      // If draft already has the full DTO (hydrated from backend), cast it to Task shape
      if (item.resolvedTask) return item.resolvedTask as unknown as Task;
      // Otherwise fall back to searching the live task list by either direction UUID
      return tasksList.find(t =>
        t.workItemUuid === item.targetWorkItemUuid ||
        t.workItemUuid === item.sourceWorkItemUuid
      ) || null;
    })
    .filter(Boolean) as Task[];

  const externalLinks = linkedItems.filter(item =>
    !item.resolvedTask &&
    !tasksList.find(t =>
      t.workItemUuid === item.targetWorkItemUuid ||
      t.workItemUuid === item.sourceWorkItemUuid
    )
  );

  return (
    <div className="w-full  relative z-10">
      {!readOnly && (
        <div 
          className="flex items-center gap-2 mb-3 bg-primary-variant px-4 py-3 rounded-lg cursor-pointer hover:bg-primary-subtle transition-colors"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          {isExpanded ? <Minus className="w-4 h-4 text-purple-500" /> : <Plus className="w-4 h-4 text-purple-500" />}
          <h3 className="font-medium text-neutral text-sm">Link work items</h3>
        </div>
      )}

      {isExpanded && !readOnly && (
        <div className="flex flex-col md:flex-row items-center gap-3 w-full relative z-[60] mb-4 transition-all animate-in fade-in slide-in-from-top-2 duration-200">
          <Combobox
            value={null}
            onChange={(task: Task | null) => {
              if (task) {
                setSelectedTask(task);
                const displayTitle = task.title || task.workItemTitle || '';
                setInputValue(`${task.workItemKey} ${displayTitle}`.trim());
              }
            }}
          >
            {({ open }) => (
              <>
                <div className="flex-1 bg-primary-variant border border-primary-subtle rounded-lg px-3 py-2 flex flex-wrap items-center min-h-[42px] relative z-10 w-full">
                  <ComboboxInput
                    displayValue={() => inputValue}
                    onChange={(e) => {
                      setInputValue(e.target.value);
                      setValidationError(null);
                      if (selectedTask) setSelectedTask(null);
                    }}
                    onClick={() => {
                      if (!open) comboboxBtnRef.current?.click();
                    }}
                    disabled={disabled}
                    placeholder="Search by ID or title"
                    className={`w-full bg-transparent outline-none text-sm text-neutral placeholder:text-neutral-medium disabled:opacity-50 ${validationError ? 'text-red-400' : ''}`}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && selectedTask) {
                        const activeElement = document.activeElement;
                        if (activeElement?.getAttribute('role') !== 'option') {
                          e.preventDefault();
                          handleAddLink();
                        }
                      }
                    }}
                  />
                  <ComboboxButton ref={comboboxBtnRef} className="hidden" />

                  {/* Combobox Dropdown anchored to the input */}
                  <ComboboxOptions className="absolute top-[46px] left-0 w-full max-h-[250px] overflow-auto rounded-lg bg-primary-variant border border-primary-subtle shadow-lg py-1 z-[100] empty:hidden">
                      {filteredTasks.length === 0 && inputValue !== '' && (
                        <div className="relative cursor-default select-none px-4 py-2 text-neutral-medium text-sm flex items-center gap-2">
                          <span className="text-amber-400">⚠</span>
                          No matching work item found
                        </div>
                      )}
                      {filteredTasks.map((task) => {
                        const typeUI = availableWorkTypes.find(t => t.workTypeUuid === task.workTypeUuid);
                        const displayTitle = task.title || task.workItemTitle || 'No Title';

                        // Safe fallback to task's native category if availableWorkTypes lookup fails
                        const category = typeUI?.workTypeCategory || task.workTypeCategory;
                        const iconData = category ? workTypeUIMap[category as keyof typeof workTypeUIMap] : null;
                        
                        const displayCategory = category || 'Unknown';

                        return (
                          <ComboboxOption
                            key={task.workItemUuid}
                            value={task}
                            className={({ focus }) =>
                              `relative cursor-default select-none py-2 pl-3 pr-4 flex items-center justify-between gap-3 ${
                                focus ? 'bg-primary-subtle text-neutral' : 'text-neutral-medium'
                              }`
                            }
                          >
                            <div className="flex items-center gap-2 min-w-0 flex-1">
                                {iconData ? (
                                  <span className={`${iconData.color} text-base shrink-0 flex items-center justify-center`}>
                                    {iconData.icon}
                                  </span>
                                ) : (
                                  <FileText className="w-4 h-4 text-teal-500 shrink-0"/>
                                )}
                                <span className="text-xs font-semibold text-neutral-light shrink-0 min-w-[60px]">{task.workItemKey}</span>
                                <span className="text-sm truncate w-[50%] flex-1">{displayTitle}</span>
                            </div>
                            <span className="text-[10px] bg-primary-subtle px-2 py-0.5 rounded text-neutral-medium shrink-0 uppercase">{displayCategory}</span>
                          </ComboboxOption>
                        );
                      })}
                  </ComboboxOptions>
                </div>
              </>
            )}
          </Combobox>

          <div className="flex flex-col items-stretch gap-2 shrink-0 w-full sm:w-auto">
            {validationError && (
              <p className="text-xs text-red-400 px-1 sm:hidden">⚠ {validationError}</p>
            )}
            <div className="flex items-center gap-2">
              <Button
                onClick={handleAddLink}
                disabled={disabled || !selectedTask}
                className="bg-primary-subtle hover:bg-primary-intermediate text-neutral text-sm font-medium px-4 h-[42px] rounded-lg transition-colors disabled:opacity-50 flex-1 sm:flex-none"
              >
                Link
              </Button>

              <Button
                onClick={() => {
                  setInputValue('');
                  setSelectedTask(null);
                  setValidationError(null);
                  setIsExpanded(false);
                }}
                disabled={disabled || !inputValue.trim()}
                className="transition-colors bg-primary-subtle rounded-lg text-neutral-medium hover:text-red-400 text-sm px-3 h-[42px] disabled:opacity-50 flex-1 sm:flex-none"
              >
                {inputValue.trim() ? <GoUnlink className="w-4 h-4" /> : 'Cancel'}
              </Button>
            </div>
            {validationError && (
              <p className="text-xs text-red-400 px-1 hidden sm:block">⚠ {validationError}</p>
            )}
          </div>
        </div>
      )}

      {internalTasks.length > 0 && projectId && (
        <div className="mb-3 border border-primary-subtle rounded-lg overflow-hidden bg-primary-variant">
           <AddTaskTableLayer 
             tasks={internalTasks}
             onUpdateTask={() => {}}
             onCreateTask={() => {}} 
             assigneeOptions={mappedUsers}
             reporterOptions={mappedUsers}
             onAssigneeChange={() => {}}
             onReporterChange={() => {}}
             onStatusChange={() => {}}
             onPriorityChange={() => {}}
             projectId={projectId!}
             projectKey={projectKey!}
             isPending={false}
             isRemoving={isUnlinking}
             removeIcon={<GoUnlink className="w-4 h-4" />}
             removeTitle="Unlink work item"
             isRemoveDestructive={false}
             visibleColumns={['workItemKey', 'title', 'workStatusUuid', 'workItemAssigneeAppUserUuid', 'workPriority']}
             isReadOnly={true}
             isScrollable={false}
             onRemove={readOnly ? undefined : (task) => {
               // Robust search for the draft item matching this task (can be id, target, or source match)
               const item = linkedItems.find(li => 
                  li.id === task.workItemUuid || 
                  li.targetWorkItemUuid === task.workItemUuid || 
                  li.sourceWorkItemUuid === task.workItemUuid
               );
               if (item) {
                 handleRemoveLink(item);
               } else {
                 console.warn('Could not find linked item for removal search:', task.workItemUuid);
               }
             }}
           />
        </div>
      )}

      {externalLinks.length > 0 && (
        <div className="space-y-2 mb-3">
          {externalLinks.map((item) => {
            // Build a human-readable label: prefer any existing key/title info,
            // otherwise show a shortened UUID prefix so it never shows a raw 36-char UUID
            const uuid = item.targetWorkItemUuid || item.sourceWorkItemUuid || '';
            const displayLabel = uuid && uuid.length === 36
              ? `Work item · ${uuid.split('-')[0].toUpperCase()}…`
              : uuid;

            return (
              <div
                key={item.id}
                className="flex items-center justify-between gap-3 bg-primary-variant border border-primary-subtle rounded-lg px-3 py-2"
              >
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <FileText className="w-4 h-4 text-neutral-medium shrink-0" />
                  <span className="text-sm text-neutral-medium truncate flex-1 italic">
                    {displayLabel}
                  </span>
                </div>
                {!readOnly && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); handleRemoveLink(item); }}
                    disabled={disabled || isUnlinking}
                    className="text-neutral-medium hover:text-red-500 transition-colors bg-primary-subtle p-2 rounded-md disabled:opacity-50"
                    title="Unlink work item"
                  >
                    <GoUnlink className="w-4 h-4" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default LinkedItemsList;
